import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Edit, Trash2, ArrowLeft, Package, Truck, User, Phone, Mail, Store, MapPin, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatPhoneNumber } from '@/lib/phoneUtils';
import { HeuresOuvertureEditor } from '@/components/fournisseurs/HeuresOuvertureEditor';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// ─── Formulaire Fournisseur ─────────────────────────────────
function FournisseurForm({ data, onChange, onSave, onCancel, isEdit }) {
  // 🆕 [2026-06-11] Magasins / succursales — gestion locale avant save
  const magasins = data.magasins || [];
  const addMagasin = () => {
    const newMag = { nom: '', adresse: '', telephone: '' };
    onChange({ ...data, magasins: [...magasins, newMag] });
  };
  const updateMagasin = (idx, field, value) => {
    const next = magasins.map((m, i) => i === idx ? { ...m, [field]: value } : m);
    onChange({ ...data, magasins: next });
  };
  const removeMagasin = (idx) => {
    onChange({ ...data, magasins: magasins.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Nom du fournisseur *</Label>
        <Input data-testid="fournisseur-nom" value={data.nom || ''} onChange={(e) => onChange({ ...data, nom: e.target.value })} placeholder="Ex: Benjamin Moore" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Adresse</Label>
          <Input value={data.adresse || ''} onChange={(e) => onChange({ ...data, adresse: e.target.value })} />
        </div>
        <div>
          <Label>Ville</Label>
          <Input value={data.ville || ''} onChange={(e) => onChange({ ...data, ville: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Province</Label>
          <Input value={data.province || 'Québec'} onChange={(e) => onChange({ ...data, province: e.target.value })} />
        </div>
        <div>
          <Label>Code postal</Label>
          <Input value={data.code_postal || ''} onChange={(e) => onChange({ ...data, code_postal: e.target.value })} />
        </div>
        <div>
          <Label>Téléphone</Label>
          <Input data-testid="fournisseur-tel" value={data.telephone || ''} onChange={(e) => onChange({ ...data, telephone: formatPhoneNumber(e.target.value) })} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Courriel</Label>
          <Input value={data.email || ''} onChange={(e) => onChange({ ...data, email: e.target.value })} />
        </div>
        <div>
          <Label>Site web</Label>
          <Input value={data.site_web || ''} onChange={(e) => onChange({ ...data, site_web: e.target.value })} />
        </div>
      </div>

      <div className="border-t pt-4 mt-4">
        <h3 className="font-semibold text-base mb-3 flex items-center gap-2"><User className="w-4 h-4" /> Représentant assigné au compte</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Nom du représentant</Label>
            <Input data-testid="rep-nom" value={data.representant_nom || ''} onChange={(e) => onChange({ ...data, representant_nom: e.target.value })} />
          </div>
          <div>
            <Label>Poste / Titre</Label>
            <Input value={data.representant_poste || ''} onChange={(e) => onChange({ ...data, representant_poste: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div>
            <Label>Téléphone du représentant</Label>
            <Input value={data.representant_telephone || ''} onChange={(e) => onChange({ ...data, representant_telephone: formatPhoneNumber(e.target.value) })} />
          </div>
          <div>
            <Label>Courriel du représentant</Label>
            <Input value={data.representant_email || ''} onChange={(e) => onChange({ ...data, representant_email: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="border-t pt-4 mt-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-base flex items-center gap-2">
            <Store className="w-4 h-4" /> Magasins / Succursales
            <span className="text-xs font-normal text-gray-500">(optionnel — pour le ramassage de matériel)</span>
          </h3>
          <Button type="button" size="sm" variant="outline" onClick={addMagasin} data-testid="add-magasin-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
          </Button>
        </div>
        {magasins.length === 0 ? (
          <p className="text-xs text-gray-500 italic px-2 py-3 bg-gray-50 border border-dashed rounded-lg">
            Aucun magasin. Si ce fournisseur n'a qu'une seule adresse, laissez vide et l'adresse principale ci-dessus sera utilisée. Sinon, ajoutez ici chaque succursale (ex: Sherwin-Williams Pierre-Bertrand, Sherwin-Williams Charlesbourg, etc.).
          </p>
        ) : (
          <div className="space-y-2">
            {magasins.map((m, idx) => (
              <div key={idx} className="border rounded-lg p-3 bg-gray-50 space-y-2" data-testid={`magasin-row-${idx}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div className="sm:col-span-1">
                      <Label className="text-xs">Nom *</Label>
                      <Input
                        value={m.nom || ''}
                        onChange={(e) => updateMagasin(idx, 'nom', e.target.value)}
                        placeholder="Ex: Pierre-Bertrand"
                        data-testid={`magasin-nom-${idx}`}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Adresse *</Label>
                      <Input
                        value={m.adresse || ''}
                        onChange={(e) => updateMagasin(idx, 'adresse', e.target.value)}
                        placeholder="Ex: 1040 boul. Pierre-Bertrand, Québec, QC G1M 3H1"
                        data-testid={`magasin-adresse-${idx}`}
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => removeMagasin(idx)}
                    className="text-red-600 hover:bg-red-50 mt-5"
                    data-testid={`magasin-delete-${idx}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div>
                  <Label className="text-xs">Téléphone</Label>
                  <Input
                    value={m.telephone || ''}
                    onChange={(e) => updateMagasin(idx, 'telephone', formatPhoneNumber(e.target.value))}
                    placeholder="(418) 682-8066"
                    data-testid={`magasin-tel-${idx}`}
                    className="sm:max-w-xs"
                  />
                </div>
                {/* 🆕 [2026-06-11 v4] Heures d'ouverture par magasin */}
                <HeuresOuvertureEditor
                  heures={m.heures_ouverture || null}
                  onChange={(h) => updateMagasin(idx, 'heures_ouverture', h)}
                  testIdPrefix={`magasin-${idx}-heures`}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 🆕 [2026-06-11 v4] Heures d'ouverture principales (pour fournisseurs sans magasin) */}
      {magasins.length === 0 && (
        <div className="border-t pt-4 mt-2">
          <HeuresOuvertureEditor
            heures={data.heures_ouverture || null}
            onChange={(h) => onChange({ ...data, heures_ouverture: h })}
            testIdPrefix="fournisseur-heures"
          />
        </div>
      )}

      <div>
        <Label>Notes</Label>
        <Input value={data.notes || ''} onChange={(e) => onChange({ ...data, notes: e.target.value })} placeholder="Notes internes..." />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
        <Button data-testid="save-fournisseur-btn" onClick={onSave}>{isEdit ? 'Modifier' : 'Créer'}</Button>
      </div>
    </div>
  );
}

// ─── Formulaire Produit ─────────────────────────────────
function ProduitForm({ data, onChange, onSave, onCancel, isEdit }) {
  // Calcul: prix_vendu = prix_detail * (1 - rabais_pct/100)
  // Profit = prix_vendu - prix_paye
  const prixDetail = parseFloat(data.prix_detail) || 0;
  const rabaisPct = parseFloat(data.rabais_pct) || 0;
  const prixVendu = prixDetail * (1 - rabaisPct / 100);
  const prixPaye = parseFloat(data.prix_paye) || 0;
  const profitDollar = prixVendu - prixPaye;
  const profitPct = prixPaye > 0 ? (profitDollar / prixPaye) * 100 : 0;
  const [newFini, setNewFini] = useState('');
  const finis = data.finis_disponibles || [];

  const addFini = () => {
    const val = newFini.trim();
    if (!val || finis.includes(val)) return;
    onChange({ ...data, finis_disponibles: [...finis, val] });
    setNewFini('');
  };

  const removeFini = (f) => {
    onChange({ ...data, finis_disponibles: finis.filter(x => x !== f) });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Nom du produit *</Label>
          <Input data-testid="produit-nom" value={data.nom || ''} onChange={(e) => onChange({ ...data, nom: e.target.value })} placeholder="Ex: Regal Select Eggshell" />
        </div>
        <div>
          <Label>Code produit</Label>
          <Input data-testid="produit-code" value={data.code_produit || ''} onChange={(e) => onChange({ ...data, code_produit: e.target.value })} placeholder="Ex: RS-550" />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <div>
          <Label>Prix payé ($) *</Label>
          <Input data-testid="produit-prix" type="number" step="0.01" value={data.prix_paye ?? ''} onChange={(e) => onChange({ ...data, prix_paye: parseFloat(e.target.value) || 0 })} />
        </div>
        <div>
          <Label>Prix détail ($) *</Label>
          <Input data-testid="produit-prix-detail" type="number" step="0.01" value={data.prix_detail ?? ''} onChange={(e) => onChange({ ...data, prix_detail: parseFloat(e.target.value) || 0 })} />
        </div>
        <div>
          <Label>Rabais client (%)</Label>
          <Input data-testid="produit-rabais" type="number" step="0.1" value={data.rabais_pct ?? ''} onChange={(e) => onChange({ ...data, rabais_pct: parseFloat(e.target.value) || 0 })} />
        </div>
        <div>
          <Label>Prix vendu (auto)</Label>
          <Input readOnly value={prixVendu.toFixed(2)} className="bg-gray-100 font-bold text-green-700" data-testid="produit-prix-vendu" />
        </div>
        <div>
          <Label>% Profit (auto)</Label>
          <Input readOnly value={`${profitPct.toFixed(1)}%`} className={`bg-gray-100 font-bold ${profitPct >= 0 ? 'text-green-700' : 'text-red-600'}`} data-testid="produit-profit-pct" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Unité *</Label>
          <Select value={data.unite || 'gallon'} onValueChange={(val) => onChange({ ...data, unite: val })}>
            <SelectTrigger data-testid="produit-unite"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pinte">Pinte</SelectItem>
              <SelectItem value="gallon">Gallon</SelectItem>
              <SelectItem value="chaudiere">Chaudière</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Couverture (pi²)</Label>
          <Input data-testid="produit-couverture" type="number" step="1" value={data.couverture_pi2 || ''} onChange={(e) => onChange({ ...data, couverture_pi2: parseFloat(e.target.value) || null })} placeholder="Ex: 400" />
        </div>
      </div>
      {/* Finis disponibles */}
      <div>
        <Label>Finis disponibles</Label>
        <div className="flex gap-2 mt-1">
          <Input
            value={newFini}
            onChange={(e) => setNewFini(e.target.value)}
            placeholder="Ex: Velours, Mat, Perle..."
            className="flex-1"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFini(); } }}
            data-testid="produit-fini-input"
          />
          <Button type="button" size="sm" onClick={addFini} data-testid="produit-fini-add">Ajouter</Button>
        </div>
        {finis.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {finis.map((f) => (
              <span key={f} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-1 rounded-full">
                {f}
                <button type="button" onClick={() => removeFini(f)} className="text-blue-600 hover:text-red-600 ml-0.5">&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
        <Button data-testid="save-produit-btn" onClick={onSave}>{isEdit ? 'Modifier' : 'Créer'}</Button>
      </div>
    </div>
  );
}

// ─── Page Principale ─────────────────────────────────
export function GestionProduits() {
  const [fournisseurs, setFournisseurs] = useState([]);
  const [selectedFournisseur, setSelectedFournisseur] = useState(null);
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchProduit, setSearchProduit] = useState('');

  // Modals
  const [showFournisseurModal, setShowFournisseurModal] = useState(false);
  const [editingFournisseur, setEditingFournisseur] = useState(null);
  const [fournisseurForm, setFournisseurForm] = useState({});

  const [showProduitModal, setShowProduitModal] = useState(false);
  const [editingProduit, setEditingProduit] = useState(null);
  const [produitForm, setProduitForm] = useState({});

  const fetchFournisseurs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fournisseurs?actif=true`);
      if (res.status !== 200) throw new Error('Erreur');
      const data = res.data;
      setFournisseurs(data.fournisseurs || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchProduits = useCallback(async (fournisseurId) => {
    try {
      const res = await axios.get(`${API_URL}/api/produits?actif=true&fournisseur_id=${fournisseurId}`);
      if (res.status !== 200) throw new Error('Erreur');
      const data = res.data;
      setProduits(data.produits || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchFournisseurs(); }, [fetchFournisseurs]);

  useEffect(() => {
    if (selectedFournisseur) fetchProduits(selectedFournisseur.id);
  }, [selectedFournisseur, fetchProduits]);

  // ─── Handlers Fournisseur ───
  const openNewFournisseur = () => {
    setEditingFournisseur(null);
    setFournisseurForm({ province: 'Québec' });
    setShowFournisseurModal(true);
  };

  const openEditFournisseur = (f) => {
    setEditingFournisseur(f);
    setFournisseurForm({ ...f });
    setShowFournisseurModal(true);
  };

  const saveFournisseur = async () => {
    if (!fournisseurForm.nom) { toast.error('Le nom est requis'); return; }
    try {
      const res = editingFournisseur
        ? await axios.put(`${API_URL}/api/fournisseurs/${editingFournisseur.id}`, fournisseurForm)
        : await axios.post(`${API_URL}/api/fournisseurs`, fournisseurForm);
      if (res.status !== 200) throw new Error('Erreur');
      toast.success(editingFournisseur ? 'Fournisseur modifié!' : 'Fournisseur créé!');
      setShowFournisseurModal(false);
      fetchFournisseurs();
    } catch (e) { toast.error('Erreur lors de la sauvegarde'); }
  };

  const deleteFournisseur = async (id) => {
    if (!window.confirm('Supprimer ce fournisseur et tous ses produits?')) return;
    try {
      await axios.delete(`${API_URL}/api/fournisseurs/${id}`);
      toast.success('Fournisseur supprimé!');
      if (selectedFournisseur?.id === id) setSelectedFournisseur(null);
      fetchFournisseurs();
    } catch (e) { toast.error('Erreur'); }
  };

  // ─── Handlers Produit ───
  const openNewProduit = () => {
    setEditingProduit(null);
    setProduitForm({ fournisseur_id: selectedFournisseur.id, unite: 'gallon', prix_paye: 0, profit_pct: 25 });
    setShowProduitModal(true);
  };

  const openEditProduit = (p) => {
    setEditingProduit(p);
    setProduitForm({ ...p });
    setShowProduitModal(true);
  };

  const saveProduit = async () => {
    if (!produitForm.nom) { toast.error('Le nom est requis'); return; }
    try {
      const res = editingProduit
        ? await axios.put(`${API_URL}/api/produits/${editingProduit.id}`, produitForm)
        : await axios.post(`${API_URL}/api/produits`, produitForm);
      if (res.status !== 200) throw new Error('Erreur');
      toast.success(editingProduit ? 'Produit modifié!' : 'Produit créé!');
      setShowProduitModal(false);
      fetchProduits(selectedFournisseur.id);
    } catch (e) { toast.error('Erreur lors de la sauvegarde'); }
  };

  const deleteProduit = async (id) => {
    if (!window.confirm('Supprimer ce produit?')) return;
    try {
      await axios.delete(`${API_URL}/api/produits/${id}`);
      toast.success('Produit supprimé!');
      fetchProduits(selectedFournisseur.id);
    } catch (e) { toast.error('Erreur'); }
  };

  const uniteLabel = (u) => ({ pinte: 'Pinte', gallon: 'Gallon', chaudiere: 'Chaudière' }[u] || u);

  if (loading) {
    return <div className="p-6"><div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div></div></div>;
  }

  // ─── Vue: Détails d'un fournisseur (ses produits) ───
  if (selectedFournisseur) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="produits-view">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => setSelectedFournisseur(null)} data-testid="back-to-fournisseurs">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{selectedFournisseur.nom}</h1>
            <div className="flex gap-4 text-sm text-gray-600 mt-1">
              {selectedFournisseur.telephone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{selectedFournisseur.telephone}</span>}
              {selectedFournisseur.representant_nom && <span className="flex items-center gap-1"><User className="w-3 h-3" />Rep: {selectedFournisseur.representant_nom}</span>}
              {selectedFournisseur.representant_telephone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{selectedFournisseur.representant_telephone}</span>}
              {selectedFournisseur.representant_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{selectedFournisseur.representant_email}</span>}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => openEditFournisseur(selectedFournisseur)}>
            <Edit className="w-4 h-4 mr-1" /> Modifier fournisseur
          </Button>
          <Button onClick={openNewProduit} data-testid="add-produit-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau Produit
          </Button>
        </div>

        {/* Barre de recherche */}
        <div className="mb-4">
          <Input
            type="text"
            placeholder="Rechercher un produit par nom, code ou fini..."
            value={searchProduit}
            onChange={e => setSearchProduit(e.target.value)}
            className="max-w-md"
            data-testid="search-produit"
          />
        </div>

        {(() => {
          const filtered = searchProduit.trim()
            ? produits.filter(p => {
                const q = searchProduit.toLowerCase();
                return (p.nom || '').toLowerCase().includes(q) ||
                  (p.code || '').toLowerCase().includes(q) ||
                  (p.finis_disponibles || []).some(f => f.toLowerCase().includes(q));
              })
            : produits;
          return filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Aucun produit pour ce fournisseur</p>
              <Button onClick={openNewProduit}>Ajouter un premier produit</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-lg shadow" data-testid="produits-table">
              <thead>
                <tr className="bg-gray-100 text-left text-sm">
                  <th className="p-3 font-semibold">Code</th>
                  <th className="p-3 font-semibold">Nom du produit</th>
                  <th className="p-3 font-semibold text-right">Prix payé</th>
                  <th className="p-3 font-semibold text-right">Prix détail</th>
                  <th className="p-3 font-semibold text-right">Rabais %</th>
                  <th className="p-3 font-semibold text-right">Prix vendu</th>
                  <th className="p-3 font-semibold text-right">Profit %</th>
                  <th className="p-3 font-semibold text-center">Unité</th>
                  <th className="p-3 font-semibold text-center w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const pDetail = p.prix_detail || 0;
                  const pRabais = p.rabais_pct || 0;
                  const pVendu = pDetail * (1 - pRabais / 100);
                  const pPaye = p.prix_paye || 0;
                  const pProfit = pPaye > 0 ? ((pVendu - pPaye) / pPaye * 100) : 0;
                  return (
                  <tr key={p.id} className="border-t hover:bg-gray-50">
                    <td className="p-3 text-sm font-mono text-gray-600">{p.code_produit || '-'}</td>
                    <td className="p-3 font-medium">{p.nom}</td>
                    <td className="p-3 text-right">{pPaye.toFixed(2)} $</td>
                    <td className="p-3 text-right">{pDetail.toFixed(2)} $</td>
                    <td className="p-3 text-right">{pRabais}%</td>
                    <td className="p-3 text-right font-bold text-green-700">{pVendu.toFixed(2)} $</td>
                    <td className={`p-3 text-right font-semibold ${pProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{pProfit.toFixed(1)}%</td>
                    <td className="p-3 text-center">{uniteLabel(p.unite)}</td>
                    <td className="p-3 text-center">
                      <div className="flex gap-1 justify-center">
                        <Button size="sm" variant="ghost" onClick={() => openEditProduit(p)} data-testid={`edit-produit-${p.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteProduit(p.id)} data-testid={`delete-produit-${p.id}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
        })()}

        {/* Modal Produit */}
        <Dialog open={showProduitModal} onOpenChange={setShowProduitModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingProduit ? 'Modifier' : 'Nouveau'} Produit</DialogTitle>
            </DialogHeader>
            <ProduitForm data={produitForm} onChange={setProduitForm} onSave={saveProduit} onCancel={() => setShowProduitModal(false)} isEdit={!!editingProduit} />
          </DialogContent>
        </Dialog>

        {/* Modal Fournisseur (édition) */}
        <Dialog open={showFournisseurModal} onOpenChange={setShowFournisseurModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Modifier Fournisseur</DialogTitle>
            </DialogHeader>
            <FournisseurForm data={fournisseurForm} onChange={setFournisseurForm} onSave={saveFournisseur} onCancel={() => setShowFournisseurModal(false)} isEdit={true} />
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─── Vue: Liste des fournisseurs ───
  return (
    <div className="p-2 sm:p-6 max-w-7xl mx-auto" data-testid="fournisseurs-view">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Gestion des Produits</h1>
          <p className="text-gray-600 mt-1 text-sm">Gérez vos fournisseurs et leurs produits</p>
        </div>
        <Button onClick={openNewFournisseur} data-testid="add-fournisseur-btn" className="w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" /> Nouveau Fournisseur
        </Button>
      </div>

      {fournisseurs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Aucun fournisseur</p>
            <Button onClick={openNewFournisseur}>Ajouter votre premier fournisseur</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {fournisseurs.map((f) => (
            <Card
              key={f.id}
              className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-blue-500"
              onClick={() => setSelectedFournisseur(f)}
              data-testid={`fournisseur-card-${f.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Truck className="w-5 h-5 text-blue-600" />
                    {f.nom}
                  </CardTitle>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => openEditFournisseur(f)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteFournisseur(f.id)}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 text-sm text-gray-600">
                  {f.telephone && <p className="flex items-center gap-2"><Phone className="w-3 h-3" />{f.telephone}</p>}
                  {f.email && <p className="flex items-center gap-2"><Mail className="w-3 h-3" />{f.email}</p>}
                  {Array.isArray(f.magasins) && f.magasins.length > 0 && (
                    <p className="flex items-center gap-2" data-testid={`fournisseur-magasins-count-${f.id}`}>
                      <Store className="w-3 h-3 text-amber-600" />
                      <span className="text-amber-700 font-medium">{f.magasins.length} succursale{f.magasins.length > 1 ? 's' : ''}</span>
                    </p>
                  )}
                  {f.representant_nom && (
                    <div className="mt-2 pt-2 border-t">
                      <p className="font-medium text-gray-800 flex items-center gap-2"><User className="w-3 h-3" />Rep: {f.representant_nom}</p>
                      {f.representant_telephone && <p className="ml-5">{f.representant_telephone}</p>}
                    </div>
                  )}
                </div>
                <p className="text-xs text-blue-600 mt-3 font-medium">Cliquer pour voir les produits →</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal Fournisseur */}
      <Dialog open={showFournisseurModal} onOpenChange={setShowFournisseurModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingFournisseur ? 'Modifier' : 'Nouveau'} Fournisseur</DialogTitle>
          </DialogHeader>
          <FournisseurForm data={fournisseurForm} onChange={setFournisseurForm} onSave={saveFournisseur} onCancel={() => setShowFournisseurModal(false)} isEdit={!!editingFournisseur} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
